import React, { useState } from 'react'
import { Plus, Loader2, Tag } from 'lucide-react'
import { Drawer } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import useActivationTypeStore from '../../stores/activationTypeStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'

interface AddActivationTypeDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const AddActivationTypeDrawer: React.FC<AddActivationTypeDrawerProps> = ({ isOpen, onClose }) => {
  const { showSuccess, showError } = useToast()
  const { createActivationType, loadActivationTypes } = useActivationTypeStore()
  const { selectedCustomer } = useGlobalCustomerStore()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [isCreating, setIsCreating] = useState(false)

  const resetForm = () => {
    setName('')
    setDescription('')
    setIsActive(true)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedCustomer?.id) {
      showError('Error', 'Please select a customer first')
      return
    }

    if (!name.trim()) {
      showError('Validation Error', 'Activation type name is required')
      return
    }

    if (name.length > 100) {
      showError('Validation Error', 'Activation type name must be 100 characters or less')
      return
    }

    setIsCreating(true)

    try {
      const newActivationType = await createActivationType(String(selectedCustomer.id), {
        name: name.trim(),
        description: description.trim() || null,
        is_active: isActive
      })

      showSuccess('Activation Type Created', `"${newActivationType.name}" has been created successfully`)

      await loadActivationTypes(String(selectedCustomer.id))

      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showError(
        'Failed to create activation type',
        errorMessage.includes('already exists')
          ? 'An activation type with this name already exists'
          : 'Please try again later'
      )
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Activation Type"
      description="Create a new activation type for categorizing activations"
      icon={<Tag className="h-5 w-5" />}
      width="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-xs font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Content Drop, Sponsorship"
            maxLength={100}
            disabled={isCreating}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-xs font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this activation type"
            rows={3}
            disabled={isCreating}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed resize-none"
          />
        </div>

        <div className="flex items-center">
          <input
            id="isActive"
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            disabled={isCreating}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="isActive" className="ml-2 block text-xs text-gray-700">
            Active (can be used in new activations)
          </label>
        </div>

        <div className="flex space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isCreating}
            className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isCreating || !name.trim()}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Create Type
              </>
            )}
          </button>
        </div>
      </form>
    </Drawer>
  )
}

export default AddActivationTypeDrawer
